/* OTERVANO: editable gallery, manual/stepped/continuous movement. No cart or tracking calls. */
(() => {
  'use strict';
  if (customElements.get('ov-media-gallery')) return;
  class OvMediaGallery extends HTMLElement {
    connectedCallback() { this.setupFrame = requestAnimationFrame(() => this.setup()); }
    setup() {
      if (this.controller || !this.isConnected) return;
      this.track = this.querySelector('.ovg-track');
      this.cards = [...this.querySelectorAll('.ovg-card:not([data-ovg-clone])')];
      if (!this.track || !this.cards.length) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.prev = this.querySelector('[data-ovg-prev]'); this.next = this.querySelector('[data-ovg-next]');
      this.counter = this.querySelector('[data-ovg-counter]'); this.dots = this.querySelector('.ovg-dots');
      this.status = this.querySelector('[data-ovg-status]'); this.toggle = this.querySelector('[data-ovg-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width:749px)');
      this.mode = this.dataset.mode || 'legacy';
      if (this.mode === 'legacy') this.mode = this.dataset.rotate === 'true' ? 'steps' : 'manual';
      this.isEditor = Boolean(window.Shopify?.designMode);
      this.editorPaused = this.isEditor && this.dataset.editorMotion !== 'true';
      this.paused = this.motion.matches || this.editorPaused;
      this.inView = false; this.hovered = false; this.index = 0;
      this.intervalMs = Math.max(3000, Math.min(12000, Number(this.dataset.interval) * 1000 || 6000));
      this.track.setAttribute('dir', 'ltr');
      this.cards.forEach(card => card.querySelectorAll('img').forEach(img => img.draggable = false));
      this.prev?.addEventListener('click', () => this.navigate(-1, true), opts);
      this.next?.addEventListener('click', () => this.navigate(1, true), opts);
      this.toggle?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.position = this.track.scrollLeft;
        this.updateToggle(); this.schedule();
      }, opts);
      this.dots?.addEventListener('click', e => {
        const dot = e.target.closest('[data-card-index]');
        if (dot) { this.stopAuto(); this.go(Number(dot.dataset.cardIndex), true); }
      }, opts);
      this.track.addEventListener('scroll', () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          if (!this.playing && this.continuous) { this.wrap(); this.position = this.track.scrollLeft; }
          this.update();
        });
      }, { ...opts, passive: true });
      this.track.addEventListener('keydown', e => {
        if (e.target !== this.track) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.navigate(e.key === 'ArrowRight' ? 1 : -1, true); }
        else if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); this.stopAuto(); this.go(e.key === 'Home' ? 0 : this.cards.length - 1, true); }
      }, opts);
      this.track.addEventListener('wheel', () => this.stopAuto(), { ...opts, passive:true });
      this.track.addEventListener('pointerdown', e => {
        this.stopAuto();
        if (e.pointerType === 'mouse' && e.button === 0 && !e.target.closest('a,button,video,input')) {
          this.drag = { id:e.pointerId, x:e.clientX, left:this.track.scrollLeft };
          this.track.setPointerCapture(e.pointerId);
        }
      }, opts);
      this.track.addEventListener('pointermove', e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        this.track.scrollLeft = this.drag.left + this.drag.x - e.clientX;
      }, opts);
      const endDrag = () => { this.drag = null; this.wrap(); this.position = this.track.scrollLeft; };
      this.track.addEventListener('pointerup', endDrag, opts);
      this.track.addEventListener('pointercancel', endDrag, opts);
      this.track.addEventListener('mouseenter', () => { if (this.dataset.pauseHover !== 'false') { this.hovered = true; this.clearTimer(); } }, opts);
      this.track.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, opts);
      this.track.addEventListener('focusin', () => this.clearTimer(), opts);
      this.track.addEventListener('focusout', () => setTimeout(() => this.isConnected && this.schedule(), 0), opts);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseVideos();
        this.schedule();
      }, opts);
      this.motion.addEventListener('change', () => { this.paused = this.motion.matches; this.rebuild(); this.updateToggle(); this.schedule(); }, opts);
      this.mobile.addEventListener('change', () => { this.rebuild(); this.schedule(); }, opts);
      document.addEventListener('shopify:block:select', e => {
        const i = this.cards.findIndex(card => card.dataset.blockId === e.detail?.blockId);
        if (i >= 0) { this.stopAuto(); this.go(i, false); }
      }, opts);
      this.cards.forEach(card => card.querySelectorAll('video').forEach(video => {
        video.addEventListener('play', () => {
          this.cards.forEach(other => other.querySelectorAll('video').forEach(v => { if (v !== video) v.pause(); }));
          this.clearTimer();
        }, opts);
        video.addEventListener('pause', () => this.schedule(), opts);
      }));
      this.rebuild();
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.inView = entries.some(e => e.isIntersecting);
          if (!this.inView) this.pauseVideos();
          this.schedule();
        }, { threshold:0.05 });
        this.visibilityObserver.observe(this);
        this.videoObserver = new IntersectionObserver(entries => {
          entries.forEach(e => { if (e.intersectionRatio < .65) e.target.pause(); });
          // The belt remains calm: playing an original video is always a deliberate action.
          if (this.mode !== 'continuous' && this.dataset.videoAutoplay === 'true' && !this.motion.matches && !this.isEditor && !document.hidden) {
            const candidate = entries.find(e => e.intersectionRatio >= .9);
            if (candidate && this.inView) { candidate.target.muted = true; candidate.target.play().catch(() => {}); }
          }
        }, { threshold:[0,.65,.9,1] });
        this.cards.forEach(c => c.querySelectorAll('video').forEach(v => this.videoObserver.observe(v)));
      } else { this.inView = true; }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          const width = this.track.clientWidth;
          if (width !== this.lastWidth) { this.lastWidth = width; this.rebuild(); this.schedule(); }
        });
        this.lastWidth = this.track.clientWidth; this.resizeObserver.observe(this.track);
      }
      this.updateToggle(); this.schedule();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame); cancelAnimationFrame(this.scrollFrame || 0); this.clearTimer();
      this.controller?.abort(); this.controller = null;
      this.pauseVideos(); this.resizeObserver?.disconnect(); this.visibilityObserver?.disconnect(); this.videoObserver?.disconnect();
      this.track?.querySelectorAll('[data-ovg-clone]').forEach(el => el.remove());
      this.dots?.replaceChildren();
    }
    cloneCard(card) {
      const copy = card.cloneNode(true);
      copy.dataset.ovgClone = ''; copy.setAttribute('aria-hidden','true'); copy.inert = true;
      copy.removeAttribute('data-block-id');
      [copy,...copy.querySelectorAll('*')].forEach(el => {
        el.removeAttribute('id'); el.removeAttribute('data-shopify-editor-block');
        el.removeAttribute('autofocus');
        if (el.matches('a,button,input,select,textarea,video,[tabindex]')) el.setAttribute('tabindex','-1');
      });
      copy.querySelectorAll('video').forEach(video => {
        const img = document.createElement('img'); img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
        if (video.poster) img.src = video.poster;
        video.replaceWith(img);
      });
      copy.querySelectorAll('img').forEach(img => { img.draggable = false; img.alt = ''; });
      return copy;
    }
    rebuild() {
      this.clearTimer();
      const fraction = this.cycle ? ((this.track.scrollLeft - this.cycle) % this.cycle + this.cycle) % this.cycle / this.cycle : 0;
      this.track.querySelectorAll('[data-ovg-clone]').forEach(el => el.remove());
      this.continuous = this.mode === 'continuous' && !this.motion.matches && !this.editorPaused && this.cards.length > 1;
      this.dataset.effectiveMode = this.continuous ? 'continuous' : this.mode === 'steps' ? 'steps' : 'manual';
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const cardWidth = this.cards[0].getBoundingClientRect().width;
      this.cycle = (cardWidth + gap) * this.cards.length;
      // No repeated cards needed if the whole sequence fits in one viewport.
      if (this.cycle <= this.track.clientWidth + gap) this.continuous = false;
      this.dataset.effectiveMode = this.continuous ? 'continuous' : this.mode === 'steps' ? 'steps' : 'manual';
      if (this.continuous) {
        const before = document.createDocumentFragment(), after = document.createDocumentFragment();
        this.cards.forEach(card => { before.append(this.cloneCard(card)); after.append(this.cloneCard(card)); });
        this.track.prepend(before); this.track.append(after);
        this.track.scrollLeft = this.cycle + fraction * this.cycle;
      }
      this.position = this.track.scrollLeft;
      this.buildPoints(); this.update();
      const controls = this.querySelector('.ovg-controls');
      if (controls) controls.hidden = this.continuous || (!this.prev && !this.next && !this.counter && !this.dots);
      this.updateToggle();
    }
    wrap() {
      if (!this.continuous || !this.cycle || this.drag) return;
      const x = this.track.scrollLeft;
      if (x < this.cycle) this.track.scrollLeft = x + this.cycle;
      else if (x >= this.cycle * 2) this.track.scrollLeft = x - this.cycle;
    }
    pauseVideos() { this.cards?.forEach(c => c.querySelectorAll('video').forEach(v => v.pause())); }
    clearTimer() { clearTimeout(this.timer); cancelAnimationFrame(this.animationFrame || 0); this.timer = null; this.animationFrame = 0; this.playing = false; this.lastTick = 0; }
    stopAuto() { this.paused = true; this.clearTimer(); this.updateToggle(); }
    updateToggle() {
      if (!this.toggle) return;
      this.toggle.hidden = this.mode === 'manual' || this.cards.length < 2 || this.motion.matches || this.editorPaused || this.cycle <= this.track.clientWidth;
      const label = this.paused ? 'Reanudar movimiento' : 'Pausar movimiento';
      this.toggle.setAttribute('aria-label',label); this.toggle.title = label;
      this.toggle.setAttribute('aria-pressed',String(this.paused));
      this.toggle.querySelector('[data-ovg-pause]').toggleAttribute('hidden', this.paused);
      this.toggle.querySelector('[data-ovg-play]').toggleAttribute('hidden', !this.paused);
    }
    canRun() {
      return this.controller && !this.paused && this.inView && !document.hidden && !this.hovered && !this.motion.matches && !this.editorPaused && !this.track.contains(document.activeElement) &&
        !this.cards.some(card => [...card.querySelectorAll('video')].some(v => !v.paused));
    }
    schedule() {
      this.clearTimer();
      if (!this.canRun() || this.mode === 'manual') return;
      if (this.continuous) {
        this.position = this.track.scrollLeft; this.playing = true;
        this.animationFrame = requestAnimationFrame(time => this.tick(time));
      } else if (this.mode === 'steps') {
        this.timer = setTimeout(() => { if (this.atEnd) this.go(0,false); else this.navigate(1,false); this.schedule(); },this.intervalMs);
      }
    }
    tick(time) {
      if (!this.canRun() || !this.continuous) { this.clearTimer(); return; }
      const dt = this.lastTick ? Math.min(64,time-this.lastTick)/1000 : 0; this.lastTick = time;
      const speed = Math.max(5,Math.min(60,Number(this.mobile.matches ? this.dataset.speedM : this.dataset.speedD) || 16));
      this.position += dt * speed * (this.dataset.direction === 'right' ? -1 : 1);
      if (this.position >= this.cycle*2) this.position -= this.cycle;
      if (this.position < this.cycle) this.position += this.cycle;
      this.track.scrollLeft = this.position;
      this.animationFrame = requestAnimationFrame(t => this.tick(t));
    }
    buildPoints() {
      const start = this.track.getBoundingClientRect().left, max = this.track.scrollWidth - this.track.clientWidth;
      this.points = [];
      this.cards.forEach((card,i) => {
        const left = Math.min(max,Math.max(0,card.getBoundingClientRect().left-start+this.track.scrollLeft));
        if (!this.points.length || left-this.points.at(-1).left > 4) this.points.push({ left,card:i });
      });
      if (this.dots) {
        this.dots.replaceChildren();
        this.points.forEach(point => {
          const b=document.createElement('button'); b.type='button';b.className='ovg-dot';b.dataset.cardIndex=point.card;
          b.setAttribute('aria-label',`Mostrar foto o video ${point.card+1}`);this.dots.append(b);
        });
      }
    }
    navigate(direction,manual) {
      if (manual) this.stopAuto();
      const i = this.continuous ? (this.index+direction+this.points.length)%this.points.length : Math.max(0,Math.min(this.index+direction,this.points.length-1));
      if (this.points[i]) this.go(this.points[i].card,manual);
    }
    go(index,announce) {
      const i=Math.max(0,Math.min(index,this.cards.length-1));
      const left=this.cards[i].getBoundingClientRect().left-this.track.getBoundingClientRect().left+this.track.scrollLeft;
      this.track.scrollTo({left,behavior:this.motion.matches||this.isEditor||this.continuous?'auto':'smooth'});
      this.position = left;
      if (announce && this.status) this.status.textContent=`Foto o video ${i+1} de ${this.cards.length}`;
    }
    update() {
      let distance=Infinity;
      this.points?.forEach((p,i)=>{const d=Math.abs(p.left-this.track.scrollLeft);if(d<distance){distance=d;this.index=i;}});
      this.atEnd=this.track.scrollLeft>=this.track.scrollWidth-this.track.clientWidth-4;
      if(this.prev)this.prev.disabled=!this.continuous&&this.track.scrollLeft<4;
      if(this.next)this.next.disabled=!this.continuous&&this.atEnd;
      if(this.counter)this.counter.textContent=`${(this.points?.[this.index]?.card||0)+1} / ${this.cards.length}`;
      this.dots?.querySelectorAll('button').forEach((dot,i)=>dot.setAttribute('aria-pressed',String(i===this.index)));
    }
  }
  customElements.define('ov-media-gallery',OvMediaGallery);
})();
